import { Typography } from '@bowtie-ins/bowkit';
import { HelpCircle } from '@bowtie-ins/bowkit/icons';
import {
  ALLOWED_PARENT_ORIGINS,
  ProductType,
  usePostMessage,
} from '@bowtie-web/common';
import { Link } from '@bowtie-web/components';
import { Box } from '@material-ui/core';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

export const ANCHOR_LINK_VARIANT_MAPPINGS = {
  recommendation: {
    postMessageKey: 'scroll-to-recommendation-anchor',
    productTypes: [
      ProductType.LIFE,
      ProductType.CRITICAL_ILLNESS,
      ProductType.VHIS,
    ],
    dataUiVariant: 'Scrolling To Recommendation Widget',
  },
  'vhis-product-choices': {
    postMessageKey: 'scroll-to-vhis-product-choices-anchor',
    productTypes: [ProductType.VHIS],
    dataUiVariant: 'Scrolling To Vhis Product Choices',
  },
  'term-level-life-premium-explanation': {
    postMessageKey: 'scroll-to-term-level-life-premium-explanation-anchor',
    productTypes: [ProductType.LIFE],
    dataUiVariant: 'Scrolling To Level Term Life Premium Explanation',
  },
};

type MarketingPageAnchorLinkProps = {
  productType: string;
  variant: keyof typeof ANCHOR_LINK_VARIANT_MAPPINGS;
};

const MarketingPageAnchorLink = ({
  productType,
  variant,
}: MarketingPageAnchorLinkProps) => {
  const { postMessage } = usePostMessage(ALLOWED_PARENT_ORIGINS);
  const shouldShowAnchor = useMemo(
    () =>
      ANCHOR_LINK_VARIANT_MAPPINGS[variant].productTypes.includes(
        productType as ProductType,
      ),
    [productType, variant],
  );

  const handleClick = useCallback(() => {
    postMessage(ANCHOR_LINK_VARIANT_MAPPINGS[variant].postMessageKey, true);
  }, [postMessage, variant]);

  if (!shouldShowAnchor) {
    return null;
  }

  return (
    <Box display="inline-flex" gridGap={2}>
      <HelpCircle style={{ fontSize: 16 }} />
      <Typography variant="body-s">
        <FormattedMessage
          id={`marketing-page-anchor-link.${variant}.description.${productType}`}
        />
      </Typography>
      <Link
        component="button"
        onClick={handleClick}
        data-ui-component="Link"
        data-ui-variant={ANCHOR_LINK_VARIANT_MAPPINGS[variant].dataUiVariant}
      >
        <Typography variant="body-s" fontWeight="medium">
          <FormattedMessage
            id={`marketing-page-anchor-link.${variant}.link.${productType}`}
          />
        </Typography>
      </Link>
    </Box>
  );
};

export default MarketingPageAnchorLink;
